import { writer } from "@/backend/write/BackendWriter";
import type { Rectangle, RelativeCoordinate } from "@/model/coordinates";
import type { Team } from "@/model/session";
import { useBoardStore } from "@/store/board";
import { useBoardsStore } from "@/store/boards";
import { useSessionStore } from "@/store/session";
import { useToastStore } from "@/store/toast";
import { zoomToRegion } from "@/utils/layout";

import { action, ctxAction, defineActions, untrackedAction } from "./actions";
import { internalActions } from "./internalActions";
import { navigationActions } from "./navigationActions";
import { screenShot } from "./utils/screenshot";

export const boardActions = defineActions("board", {
  screenshot: action(
    (event?: KeyboardEvent) => {
      screenShot();
      event?.preventDefault();
    },
    { name: /*$t*/ "contextMenu.saveBoardImage" },
  ),
  confirmSyncBacklog: untrackedAction(
    (ctx) => navigationActions.openModal(ctx, "ConfirmSyncBacklogModal"),
    { name: /*$t*/ "contextMenu.synchronizeBacklog" },
  ),
  syncBacklog: ctxAction(async (ctx, artId?: number) => {
    ctx.track({ almSyncStatus: useSessionStore().current.almStatus || undefined });
    await writer.syncBacklog(artId);
  }),
  confirmSyncIteration: untrackedAction(
    (ctx, iteration: number | null, status: string | null) =>
      navigationActions.openModal(ctx, "ConfirmSyncIterationModal", {
        attrs: { iteration, status },
      }),
    { name: /*$t*/ "contextMenu.synchronizeIteration" },
  ),
  syncIteration: ctxAction(async (ctx, team: Team, iteration: number) => {
    ctx.track({ almSyncStatus: useSessionStore().current.almStatus || undefined });
    await writer.syncIteration(team.id, iteration);
  }),
  zoomToRegion: action((bounds: Rectangle<RelativeCoordinate>) => zoomToRegion(bounds), {
    name: /*$t*/ "contextMenu.zoomToRegion",
  }),
  toggleCardSelection: action((cardId: string) => {
    const board = useBoardStore().currentBoard();
    if (cardId in board.selected) {
      unselectSticky(cardId);
    } else {
      selectSticky(cardId);
    }
  }),
  clearCardSelection: action(() => {
    useBoardStore().setActiveCardId(null);
    const board = useBoardStore().currentBoard();
    internalActions.clearCardSelection(board);
  }),
  selectMultipleStickies: action((stickyIds: string[]) => {
    useBoardStore().setSelectedStickies(stickyIds);
    const boardId = useBoardStore().currentBoard().id;
    for (const id of stickyIds) {
      writer.startAlterCard(boardId, id);
    }
  }),
  setVelocity: action((boardId: string, iteration: number, velocity: number) => {
    useBoardsStore().setVelocity({ id: boardId, iteration, velocity });
    writer.setVelocity(boardId, iteration, velocity);
  }),
  smallerCardSize: action(
    () => {
      const board = useBoardStore().currentBoard();
      internalActions.setCardSize(board.id, board.cardSize.factor - 0.5);
    },
    { name: /*$t*/ "board.smallerCards" },
  ),
  biggerCardSize: action(
    () => {
      const board = useBoardStore().currentBoard();
      internalActions.setCardSize(board.id, board.cardSize.factor + 0.5);
    },
    { name: /*$t*/ "board.biggerCards" },
  ),
  reloadKanbanConfigs: action(
    async (reload: () => Promise<void>) => {
      try {
        await reload();
        useToastStore().show(/*$t*/ "kanban.reloadOk");
      } catch (_) {
        useToastStore().show(/*$t*/ "kanban.reloadFail");
      }
    },
    { name: /*$t*/ "board.reloadKanbanConfigs", icon: "system/refresh" },
  ),
});

function selectSticky(stickyId: string) {
  useBoardStore().selectSticky(stickyId);
  writer.startAlterCard(useBoardStore().currentBoard().id, stickyId);
}

function unselectSticky(stickyId: string) {
  useBoardStore().unselectSticky(stickyId);
  writer.stopAlterCard(useBoardStore().currentBoard().id, stickyId);
}
